import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  makeStyles,
  useTheme
} from "@material-ui/core";
import { APP_VERSION } from '../../config/version';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  sectionHeader: {
    background: theme.mode === 'dark' 
      ? 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' 
      : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionContent: {
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
  },
  settingItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    transition: 'all 0.2s ease',
    height: '100%',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e3f2fd',
      borderColor: theme.mode === 'dark' ? theme.palette.primary.light : '#1976d2',
    },
  },
  settingLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  versionBadge: {
    fontSize: '16px',
    fontWeight: 700,
    padding: '6px 16px',
  },
  infoBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#1e3a8a' : '#e3f2fd',
    border: `1px solid ${theme.mode === 'dark' ? '#3b82f6' : '#1976d2'}`,
    marginTop: theme.spacing(2),
  },
}));

export default function VersionInfo() {
  const classes = useStyles();
  const theme = useTheme();
  const [versionInfo, setVersionInfo] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(true);

  useEffect(() => {
    const loadVersionInfo = async () => {
      setLoadingVersion(true);
      try {
        const response = await api.get('/version/version');
        if (response.data) {
          setVersionInfo(response.data);
        }
      } catch (error) {
        console.error('Error cargando información de versión:', error);
      } finally {
        setLoadingVersion(false);
      }
    };
    loadVersionInfo();
  }, []);

  if (loadingVersion) {
    return (
      <Box className={classes.container}>
        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionContent}>
            <Box className={classes.loadingContainer}>
              <CircularProgress size={40} />
              <Typography variant="body2" style={{ color: '#6B7280' }}>
                Cargando información de versión...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* ✅ INFORMACIÓN PRINCIPAL DE VERSIÓN */}
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label="Versión">📦</span> Información de Versión del Sistema
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Versión Actual
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                  <Chip
                    label={`v${versionInfo?.version || APP_VERSION}`}
                    className={classes.versionBadge}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                    }}
                  />
                  {versionInfo?.status && (
                    <Chip
                      label={versionInfo.status}
                      size="small"
                      style={{
                        backgroundColor: versionInfo.status === 'Estable' ? '#10b981' : '#f59e0b',
                        color: 'white',
                        fontSize: '11px'
                      }}
                    />
                  )}
                </Box>
                {versionInfo?.releaseDate && (
                  <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#6B7280' }}>
                    Fecha de Release: {new Date(versionInfo.releaseDate).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Tipo de Cambio
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Chip
                    label={versionInfo?.changeType || 'MINOR'}
                    size="small"
                    style={{
                      backgroundColor: versionInfo?.changeType === 'MAJOR' ? '#ef4444' : 
                                       versionInfo?.changeType === 'MINOR' ? '#3b82f6' : '#10b981',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 600
                    }}
                  />
                </Box>
                {versionInfo?.description && (
                  <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#9CA3AF', fontStyle: 'italic', lineHeight: 1.4 }}>
                    {versionInfo.description.length > 150 
                      ? `${versionInfo.description.substring(0, 150)}...` 
                      : versionInfo.description}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {versionInfo?.description && versionInfo.description.length > 150 && (
            <Box className={classes.infoBox}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: theme.palette.text.primary }}>
                Descripción Completa
              </Typography>
              <Typography variant="body2" style={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                {versionInfo.description}
              </Typography>
            </Box>
          )}

          <Divider style={{ margin: '20px 0' }} />

          <Grid container spacing={2}>
            {versionInfo?.nodeVersion && (
              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.settingItem}>
                  <Typography className={classes.settingLabel}>
                    Versión Node.js
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                    {versionInfo.nodeVersion}
                  </Typography>
                </Box>
              </Grid>
            )}
            {versionInfo?.platform && (
              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.settingItem}>
                  <Typography className={classes.settingLabel}>
                    Plataforma
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500, textTransform: 'capitalize' }}>
                    {versionInfo.platform}
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Versión Frontend
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  v{APP_VERSION}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Última Actualización
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  {versionInfo?.timestamp 
                    ? new Date(versionInfo.timestamp).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                    : 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider style={{ margin: '20px 0' }} />
          
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="caption" style={{ color: '#6B7280' }}>
              Sistema de versionado semántico implementado
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.href = '/releases'}
              style={{
                textTransform: 'none',
                borderRadius: 8,
                fontSize: '12px',
                padding: '6px 16px'
              }}
            >
              Ver Historial Completo →
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

